import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import InvitePreview, { DetailsFace } from '@/components/InvitePreview';
import { invitesApi } from '@/lib/invitesApi';
import { CalendarPlus, MapPin, Check, Gift, Sparkle, SealCheck, Megaphone, Users } from '@phosphor-icons/react';

// A celebratory burst when a guest says yes. Respects reduced-motion.
function celebrate(accent) {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const colors = [accent || '#2563EB', '#FFD966', '#FF6D57', '#7DD3A8', '#ffffff'];
  const fire = (ratio, opts) => confetti({ particleCount: Math.floor(120 * ratio), spread: 70, origin: { y: 0.7 }, colors, ...opts });
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
}

const field = 'w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

// Normalize an event to calendar start/end stamps (local floating time, 2h default).
function calStamps(ev) {
  const d = (ev.event_date || '').replace(/-/g, '');
  if (!/^\d{8}$/.test(d)) return null;
  const st = (ev.start_time || '18:00').replace(':', '').padEnd(4, '0').slice(0, 4);
  // End = start + 2h (events rarely carry an end time); roll the hour, cap at 23.
  const sh = Math.min(parseInt(st.slice(0, 2), 10) || 18, 23);
  const eh = Math.min(sh + 2, 23);
  const et = String(eh).padStart(2, '0') + st.slice(2);
  return { start: `${d}T${st}00`, end: `${d}T${et}00` };
}

function gcalLink(ev) {
  const s = calStamps(ev);
  if (!s) return null;
  const params = new URLSearchParams({ action: 'TEMPLATE', text: ev.title || 'Event', dates: `${s.start}/${s.end}`, location: ev.location || '' });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// A downloadable .ics — works with Apple Calendar, Outlook, and every other app.
function icsDataUrl(ev) {
  const s = calStamps(ev);
  if (!s) return null;
  const esc = (t) => String(t || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const uid = `${(ev.event_date || '').replace(/-/g, '')}-${(ev.title || 'event').replace(/\W/g, '').slice(0, 20)}@bookr.ai`;
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Bookr.ai//Invite//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${s.start}`, `DTSTART:${s.start}`, `DTEND:${s.end}`,
    `SUMMARY:${esc(ev.title || 'Event')}`,
  ];
  if (ev.location) lines.push(`LOCATION:${esc(ev.location)}`);
  lines.push('STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR');
  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
}

// Scroll-reveal wrapper — CSS drives it where supported; this adds the
// .inv-inview class as a fallback for browsers without scroll-driven animations.
function Reveal({ children, className = '', ...rest }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { el.classList.add('inv-inview'); io.unobserve(el); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`inv-reveal ${className}`} {...rest}>{children}</div>;
}

// ── Shared content sections (used by both the Card and the Flyer page layout) ──
function DetailsSection({ inv, ev, gcal, ics, mapUrl }) {
  const icsName = `${(ev.title || 'event').replace(/[^\w]+/g, '-').slice(0, 40)}.ics`;
  return (
    <div className="w-full bg-card rounded-2xl border border-border/70 shadow-soft p-5 text-center">
      <h1 className="text-xl font-display font-semibold text-foreground">{ev.title || "You're invited"}</h1>
      {inv.host_name && <p className="text-sm text-muted-foreground mt-0.5">Hosted by {inv.host_name}</p>}
      <div className="flex flex-col items-center gap-1 mt-3 text-sm text-foreground/80">
        {ev.event_date && <span>{ev.event_date}{ev.start_time ? ` · ${ev.start_time}` : ''}</span>}
        {ev.location && <span className="text-muted-foreground">{ev.location}</span>}
      </div>
      <div className="flex flex-wrap gap-2 justify-center mt-4">
        {/* Add to calendar — Google via link, everything else (Apple/Outlook) via
            a universal .ics download. */}
        {gcal && <a href={gcal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary/5"><CalendarPlus size={13} /> Google Calendar</a>}
        {ics && <a href={ics} download={icsName} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary/5"><CalendarPlus size={13} /> Apple / Outlook</a>}
        {mapUrl && <a href={mapUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground border border-border rounded-full px-3 py-1.5 hover:bg-secondary/60"><MapPin size={13} /> Map</a>}
      </div>
      {inv.registry_links?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 justify-center">
          {inv.registry_links.map((r, i) => <a key={i} href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Gift size={12} /> {r.label || 'Registry'}</a>)}
        </div>
      )}
    </div>
  );
}

// "Who's coming" social proof — a gentle nudge shown BEFORE the event (teaser/
// soon phases), when the full day-of live hub isn't up yet. Encourages RSVPs by
// showing momentum ("12 going"). Reuses the same public /live counts.
function WhosComing({ inviteId }) {
  const [c, setC] = React.useState(null);
  const [phase, setPhase] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    invitesApi.live(inviteId).then((l) => { if (alive) { setC(l.counts || {}); setPhase(l.phase); } }).catch(() => {});
    return () => { alive = false; };
  }, [inviteId]);
  // The live hub already shows headcount during soon/live/recap — don't double up.
  if (!c || ['soon', 'live', 'recap'].includes(phase)) return null;
  const going = c.headcount || 0;
  const maybe = c.maybe || 0;
  if (going < 1 && maybe < 1) return null;   // nothing worth showing yet
  return (
    <div className="w-full flex items-center justify-center gap-2 text-sm text-foreground/80" data-testid="whos-coming">
      <Users size={15} className="text-primary shrink-0" />
      <span>
        <b className="text-foreground">{going}</b> {going === 1 ? 'person is' : 'people are'} coming
        {maybe > 0 && <span className="text-muted-foreground"> · {maybe} maybe</span>}
        <span className="text-muted-foreground"> — join them!</span>
      </span>
    </div>
  );
}

function RsvpSection({ inv, form, setForm, done, err, submit }) {
  if (done) return (
    <div className="w-full bg-card rounded-2xl border border-border/70 shadow-soft p-6 text-center" data-testid="rsvp-done">
      <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2"><Check size={24} weight="bold" /></div>
      <p className="font-semibold text-foreground">Thank you, {form.name.split(' ')[0]}!</p>
      <p className="text-sm text-muted-foreground">Your RSVP{form.status === 'yes' ? ' — see you there!' : ' has been recorded.'}</p>
    </div>
  );
  return (
    <div className="w-full bg-card rounded-2xl border border-border/70 shadow-soft p-5" data-testid="rsvp-form">
      <p className="text-sm font-semibold text-foreground mb-3">Will you attend?</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[['yes', 'Yes'], ['maybe', 'Maybe'], ['no', 'No']].map(([v, l]) => (
          <button key={v} onClick={() => setForm((f) => ({ ...f, status: v }))}
            className={`py-2 rounded-lg text-sm font-semibold border ${form.status === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-secondary/60'}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2">
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className={field} />
        <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email (optional)" className={field} />
        {form.status === 'yes' && (
          <label className="flex items-center justify-between text-sm text-muted-foreground px-1">
            Bringing guests?
            <input type="number" min="0" max="10" value={form.plus_ones} onChange={(e) => setForm((f) => ({ ...f, plus_ones: e.target.value }))} className="w-16 rounded border border-border px-2 py-1 text-center" />
          </label>
        )}
        {(inv.questions || []).map((q) => (
          <div key={q.id}>
            <label className="text-xs font-medium text-muted-foreground">{q.label}</label>
            {q.type === 'dropdown' || q.type === 'multiple_choice' ? (
              <select value={form.answers[q.id] || ''} onChange={(e) => setForm((f) => ({ ...f, answers: { ...f.answers, [q.id]: e.target.value }, meal: q.id === 'meal' ? e.target.value : f.meal }))} className={field}>
                <option value="">Select…</option>
                {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : q.type === 'yes_no' ? (
              <select value={form.answers[q.id] || ''} onChange={(e) => setForm((f) => ({ ...f, answers: { ...f.answers, [q.id]: e.target.value } }))} className={field}>
                <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
            ) : (
              <input value={form.answers[q.id] || ''} onChange={(e) => setForm((f) => ({ ...f, answers: { ...f.answers, [q.id]: e.target.value } }))} className={field} />
            )}
          </div>
        ))}
      </div>
      {err && <p className="text-xs text-accent-coral mt-2">{err}</p>}
      <button onClick={submit} className="w-full mt-4 bg-primary text-primary-foreground rounded-xl h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90" data-testid="rsvp-submit">Send RSVP</button>
    </div>
  );
}

function EventTeamSection({ team }) {
  if (!team || !team.length) return null;
  return (
    <div className="w-full bg-card rounded-2xl border border-border/70 shadow-soft p-5" data-testid="event-team">
      <div className="flex items-center gap-1.5 mb-1">
        <Sparkle size={15} weight="fill" className="text-primary" />
        <h2 className="text-sm font-semibold text-foreground">The event team</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Booked through Bookr to make this one special.</p>
      <div className="space-y-2.5">
        {team.map((v) => (
          <a key={v.profile_id || v.business_name} href={v.profile_id ? `/providers/${v.profile_id}` : undefined}
            target="_blank" rel="noreferrer"
            className={`flex items-center gap-3 rounded-xl border border-border/70 p-2.5 ${v.profile_id ? 'hover:border-primary/40 hover:bg-secondary/40' : ''}`}
            data-testid="team-vendor">
            {v.photo
              ? <img src={v.photo} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
              : <div className="w-11 h-11 rounded-lg bg-secondary flex items-center justify-center text-base shrink-0">🎪</div>}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-foreground truncate">{v.business_name}</span>
                {v.verified && <SealCheck size={13} weight="fill" className="text-primary shrink-0" />}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {v.category}{v.city ? ` · ${v.city}` : ''}
                {v.review_count > 0 && <span className="text-foreground/70"> · ★ {v.rating} ({v.review_count})</span>}
              </div>
            </div>
          </a>
        ))}
      </div>
      <a href="/browse" target="_blank" rel="noreferrer" className="mt-3 block text-center text-xs font-semibold text-primary hover:underline">
        Planning your own event? Book trusted pros on Bookr →
      </a>
    </div>
  );
}

// Personalized greeting — shown when the guest opened their own per-guest link.
function GuestGreeting({ guest }) {
  if (!guest || !guest.first_name) return null;
  return (
    <div className="w-full bg-primary/8 border border-primary/20 rounded-2xl px-5 py-4 text-center" data-testid="guest-greeting">
      <p className="text-base font-display font-semibold text-foreground">Hey {guest.first_name} 👋</p>
      {guest.note
        ? <p className="text-sm text-foreground/80 mt-1 italic">“{guest.note}”</p>
        : <p className="text-sm text-muted-foreground mt-0.5">You're personally invited.</p>}
    </div>
  );
}

function DetailsCardSection({ inv, ev }) {
  const dc = inv.design?.detailsCard || {};
  const hasItems = (dc.items || []).some((i) => (i.value || '').trim());
  const showQr = inv.design?.showQr;
  if (!hasItems && !showQr) return null;
  return (
    <div data-testid="public-details-card">
      <DetailsFace design={inv.design} event={ev} scale={320} rsvpUrl={window.location.href} />
    </div>
  );
}

// Day-of live hub — headcount, host announcements, and a guest hype wall.
// Polls every 15s while open; only shown when the event is soon/live/recap.
function LiveHubSection({ inviteId, initial }) {
  const [live, setLive] = React.useState(initial || null);
  const [name, setName] = React.useState('');
  const [text, setText] = React.useState('');
  const [posting, setPosting] = React.useState(false);
  const [err, setErr] = React.useState('');

  const refresh = React.useCallback(() => {
    invitesApi.live(inviteId).then(setLive).catch(() => {});
  }, [inviteId]);

  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  if (!live || !['soon', 'live', 'recap'].includes(live.phase)) return null;
  const c = live.counts || {};
  const label = live.phase === 'live' ? "It's happening" : live.phase === 'recap' ? 'That was fun' : 'Almost time';
  const dot = live.phase === 'live';

  const post = async () => {
    const t = text.trim();
    if (!t) { setErr('Say something first!'); return; }
    setErr(''); setPosting(true);
    try {
      const r = await invitesApi.postWall(inviteId, { name: name.trim() || 'Guest', text: t });
      setText('');
      if (r.post) setLive((l) => ({ ...l, wall: [r.post, ...(l.wall || [])] }));
      else refresh();
    } catch (e) { setErr(e?.response?.data?.detail || 'Couldn’t post — try again.'); }
    finally { setPosting(false); }
  };

  return (
    <div className="w-full bg-card rounded-2xl border border-primary/20 shadow-soft overflow-hidden" data-testid="live-hub">
      <div className="px-5 pt-4 pb-3" style={{ background: 'linear-gradient(180deg, hsl(var(--primary)/0.08), transparent)' }}>
        <div className="flex items-center gap-2">
          {dot && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" /></span>}
          <h2 className="text-sm font-semibold text-foreground">{label}</h2>
        </div>
        {/* Headcount */}
        <div className="flex gap-4 mt-2 text-sm">
          <span className="text-foreground"><b className="text-lg">{c.headcount || 0}</b> going</span>
          {c.maybe > 0 && <span className="text-muted-foreground"><b>{c.maybe}</b> maybe</span>}
        </div>
      </div>

      {/* Host announcements */}
      {(live.announcements || []).length > 0 && (
        <div className="px-5 py-3 border-t border-border/60 space-y-2">
          {live.announcements.map((a) => (
            <div key={a.id} className="flex gap-2 items-start">
              <Megaphone size={15} weight="fill" className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-foreground leading-snug">{a.text}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{live.host_name || 'Host'} · {timeAgo(a.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Guest wall */}
      <div className="px-5 py-3 border-t border-border/60">
        <p className="text-xs font-semibold text-muted-foreground mb-2">Guest wall</p>
        <div className="flex gap-2 mb-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={field + ' w-24 shrink-0'} maxLength={40} />
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !posting) post(); }}
            placeholder="Leave a hype message…" className={field + ' flex-1'} maxLength={240} data-testid="wall-input" />
          <button onClick={post} disabled={posting} className="shrink-0 rounded-lg bg-primary text-primary-foreground px-3 text-sm font-semibold disabled:opacity-60" data-testid="wall-post">
            {posting ? '…' : 'Post'}
          </button>
        </div>
        {err && <p className="text-xs text-accent-coral mb-2">{err}</p>}
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {(live.wall || []).length === 0 && <p className="text-xs text-muted-foreground">Be the first to say something 🎉</p>}
          {(live.wall || []).map((w) => (
            <div key={w.id} className="rounded-xl bg-secondary/50 px-3 py-2" data-testid="wall-post-item">
              <p className="text-sm text-foreground leading-snug"><span className="font-semibold">{w.name}</span> {w.text}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(w.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function PublicInvitePage() {
  const { inviteId } = useParams();
  const [inv, setInv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', status: '', plus_ones: 0, meal: '', answers: {} });
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const [sp] = useSearchParams();
  const guestId = sp.get('g') || '';

  useEffect(() => {
    invitesApi.publicInvite(inviteId, guestId).then((d) => {
      setInv(d);
      // Personalized link: prefill the guest's name + id so RSVP is one tap.
      if (d.guest) setForm((f) => ({ ...f, name: d.guest.name || f.name, guest_id: d.guest.guest_id }));
    }).catch(() => setNotFound(true));
  }, [inviteId, guestId]);

  const submit = async () => {
    setErr('');
    if (!form.name.trim()) return setErr('Please add your name.');
    if (!form.status) return setErr('Let us know if you can make it.');
    try {
      await invitesApi.rsvp(inviteId, { ...form, plus_ones: +form.plus_ones || 0 });
      setDone(true);
      if (form.status === 'yes') celebrate(inv?.design?.colors?.accent);
    } catch { setErr('Could not submit — please try again.'); }
  };

  if (notFound) return <div className="min-h-screen bg-hero-wash flex items-center justify-center"><p className="text-muted-foreground">This invitation could not be found.</p></div>;
  if (!inv) return <div className="min-h-screen bg-hero-wash flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const ev = inv.event || {};
  const gcal = gcalLink(ev);
  const ics = icsDataUrl(ev);
  const mapUrl = ev.location ? `https://maps.google.com/?q=${encodeURIComponent(ev.location)}` : null;
  const isFlyer = inv.format === 'flyer';
  const rsvpProps = { inv, form, setForm, done, err, submit };

  // ── Flyer = a scrollable event page: parallax hero + revealing sections ──
  if (isFlyer) {
    const accent = inv.design?.colors?.accent || '#2563EB';
    return (
      <div className="min-h-screen bg-hero-wash" data-testid="public-invite" data-layout="flyer">
        {/* Parallax hero */}
        <div className="relative overflow-hidden px-4 pt-10 pb-8 flex justify-center"
          style={{ background: `linear-gradient(180deg, ${accent}14, transparent 70%)` }}>
          <div className="inv-flyer-hero w-full max-w-[440px]">
            <InvitePreview design={inv.design} format={inv.format} mode="interactive" scale={440} />
          </div>
        </div>
        <div className="max-w-md mx-auto px-4 pb-14 flex flex-col gap-4">
          {inv.guest && <Reveal><GuestGreeting guest={inv.guest} /></Reveal>}
          <Reveal><LiveHubSection inviteId={inviteId} /></Reveal>
          <Reveal><DetailsSection inv={inv} ev={ev} gcal={gcal} ics={ics} mapUrl={mapUrl} /></Reveal>
          {(() => { const s = <DetailsCardSection inv={inv} ev={ev} />; return s ? <Reveal className="flex justify-center">{s}</Reveal> : null; })()}
          <Reveal><WhosComing inviteId={inviteId} /></Reveal>
          <Reveal><RsvpSection {...rsvpProps} /></Reveal>
          <Reveal><EventTeamSection team={inv.event_team} /></Reveal>
          <p className="text-[11px] text-muted-foreground mt-2 text-center">Powered by <span className="font-semibold text-foreground">Bookr.ai</span> Invitations</p>
        </div>
      </div>
    );
  }

  // ── Card = the classic centered single-card layout ──
  return (
    <div className="min-h-screen bg-hero-wash py-10 px-4" data-testid="public-invite" data-layout="card">
      <div className="max-w-md mx-auto flex flex-col items-center gap-4">
        <div className="mb-4">
          <InvitePreview design={inv.design} format={inv.format} mode="interactive" scale={320} />
        </div>
        {inv.guest && <GuestGreeting guest={inv.guest} />}
        <LiveHubSection inviteId={inviteId} />
        {(() => { const s = <DetailsCardSection inv={inv} ev={ev} />; return s ? <div className="flex justify-center">{s}</div> : null; })()}
        <DetailsSection inv={inv} ev={ev} gcal={gcal} ics={ics} mapUrl={mapUrl} />
        <WhosComing inviteId={inviteId} />
        <RsvpSection {...rsvpProps} />
        <EventTeamSection team={inv.event_team} />
        <p className="text-[11px] text-muted-foreground mt-2">Powered by <span className="font-semibold text-foreground">Bookr.ai</span> Invitations</p>
      </div>
    </div>
  );
}
